import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Users, TrendingUp, Shield, Activity, Clock, BarChart3, CheckCircle2, Building2, Sparkles } from 'lucide-react';
import ContactForm from '../components/ContactForm';

const B2BPage = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 50 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-100px" },
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const levers = [
    { icon: Activity, title: 'Mouvement et prévention', description: 'Réduire les blessures et TMS liés au travail' },
    { icon: Shield, title: 'Gestion du stress', description: 'Capacité de récupération des équipes' },
    { icon: Clock, title: 'Sommeil et rythmes', description: 'Cycles biologiques et vigilance' },
    { icon: BarChart3, title: 'Nutrition et énergie', description: 'Stabilité énergétique au travail' },
  ];

  const impacts = [
    { label: 'Présence', value: 'Régularité et engagement renforcés' },
    { label: 'Énergie', value: 'Stabilité quotidienne des équipes' },
    { label: 'Récupération', value: 'Meilleure capacité de rebond' },
    { label: 'Cohésion', value: 'Équipes plus engagées et soudées' },
    { label: 'Prévention', value: 'Réduction du risque de burnout' },
    { label: 'Continuité', value: 'Amélioration opérationnelle' },
  ];

  const differentiators = [
    { title: 'Partenariat long terme', description: 'Pas une prestation ponctuelle, mais un accompagnement structuré dans la durée' },
    { title: 'Approche mesurable', description: 'Indicateurs de suivi précis, marqueurs d\'engagement et de régularité' },
    { title: 'Performance durable', description: 'La santé comme condition de la performance, pas un bonus "bien-être"' },
    { title: 'Expert et neutre', description: 'Un regard extérieur, structuré et professionnel sur le capital humain' },
  ];

  return (
    <div data-testid="b2b-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden pt-24">
        <div className="absolute inset-0">
          <motion.div
            className="absolute inset-0 z-10"
            animate={{ 
              background: [
                'radial-gradient(ellipse at 80% 30%, rgba(143, 188, 143, 0.12) 0%, rgba(5, 5, 5, 0) 50%)',
                'radial-gradient(ellipse at 60% 60%, rgba(152, 212, 187, 0.12) 0%, rgba(5, 5, 5, 0) 50%)',
                'radial-gradient(ellipse at 80% 30%, rgba(143, 188, 143, 0.12) 0%, rgba(5, 5, 5, 0) 50%)',
              ]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
          <img
            src="https://images.unsplash.com/photo-1762433813475-e6b761cc23d0?auto=format&fit=crop&w=2000&q=80"
            alt="Formes géométriques abstraites"
            className="absolute inset-0 w-full h-full object-cover opacity-30"
            onLoad={(e) => e.target.classList.add('loaded')}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sage/10 border border-sage/20 mb-6"
              >
                <Building2 size={16} className="text-sage" />
                <span className="font-body text-sm tracking-wide text-sage">
                  Accompagnement Entreprises
                </span>
              </motion.div>
              
              <motion.h1 
                className="font-heading text-5xl md:text-6xl lg:text-7xl font-medium text-white leading-[1.1] mb-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                Capital humain
                <span className="text-sage block">performance durable</span>
              </motion.h1>
              
              <motion.p 
                className="font-body text-lg text-gray-300 mb-8 leading-relaxed max-w-lg"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
              >
                Des équipes en meilleure santé sont des équipes plus présentes, 
                plus engagées et plus fiables. C'est un levier de performance, 
                pas un "bonus bien-être".
              </motion.p>

              <motion.div 
                className="flex flex-col sm:flex-row gap-4 mb-8"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8 }}
              >
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link
                    to="/contact"
                    data-testid="b2b-hero-cta"
                    className="bg-sage text-black font-body font-semibold px-8 py-4 rounded-full hover:bg-sage-300 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    Planifier un échange stratégique
                    <ArrowRight size={20} />
                  </Link>
                </motion.div>
              </motion.div>

              <motion.p 
                className="font-body text-sm text-gray-500"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
              >
                Rendez-vous de 30 minutes • Réponse sous 24h
              </motion.p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9, x: 50 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden lg:block"
            >
              <motion.div 
                className="relative"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.5 }}
              >
                <img
                  src="https://images.unsplash.com/photo-1758260990024-c8ad2660f1ff?auto=format&fit=crop&w=800&q=80"
                  alt="Montagnes en couches"
                  className="rounded-3xl w-full h-[500px] object-cover"
                  onLoad={(e) => e.target.classList.add('loaded')}
                />
                <motion.div 
                  className="absolute -bottom-6 -left-6 glass rounded-2xl p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <p className="font-body text-sm text-gray-400 mb-2">Approche</p>
                  <p className="font-heading text-2xl text-sage">Partenariat stratégique</p>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="max-w-3xl">
            <span className="font-body text-sm tracking-[0.2em] uppercase text-sage mb-4 block">
              Notre proposition
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">
              Un partenaire externe, expert et neutre
            </h2>
            <p className="font-body text-lg text-gray-400 leading-relaxed mb-6">
              Les entreprises qui souhaitent structurer une démarche professionnelle 
              de santé et de bien-être au travail ont besoin d'un accompagnement 
              qui va au-delà des actions bien-être ponctuelles.
            </p>
            <motion.p 
              className="font-body text-lg text-white leading-relaxed"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              EasyCoaching accompagne vos collaborateurs vers une énergie stable, 
              une présence de qualité et un engagement soutenable dans le temps.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Levers Section */}
      <section className="py-24 md:py-32 bg-[#050505] relative overflow-hidden">
        <motion.div
          className="absolute right-0 top-0 w-[500px] h-[500px] rounded-full bg-sage/5 blur-[150px]"
          animate={{ y: [0, 50, 0] }}
          transition={{ duration: 15, repeat: Infinity }}
        />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div {...fadeUp} className="mb-16">
            <span className="font-body text-sm tracking-[0.2em] uppercase text-sage mb-4 block">
              Nos leviers d'action
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
              Une approche professionnelle et mesurable
            </h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {levers.map((lever, index) => (
              <motion.div
                key={lever.title}
                variants={staggerItem}
                whileHover={{ y: -8, scale: 1.02 }}
                className="p-8 rounded-2xl bg-[#0F0F0F] border border-white/5 hover:border-sage/20 transition-all duration-500 group"
              >
                <motion.div 
                  className="w-14 h-14 rounded-xl bg-sage/10 flex items-center justify-center mb-6 group-hover:bg-sage/20 transition-colors"
                  whileHover={{ rotate: 5 }}
                >
                  <lever.icon className="text-sage" size={28} />
                </motion.div>
                <h3 className="font-heading text-2xl text-white mb-3">
                  {lever.title}
                </h3>
                <p className="font-body text-gray-400">
                  {lever.description}
                </p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            {...fadeUp} 
            className="mt-12 p-8 rounded-2xl bg-sage/5 border border-sage/20"
          >
            <div className="flex items-start gap-4">
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <BarChart3 className="text-sage flex-shrink-0 mt-1" size={24} />
              </motion.div>
              <div>
                <h4 className="font-heading text-xl text-white mb-2">Suivi et mesure</h4>
                <p className="font-body text-gray-400">
                  Indicateurs de suivi précis • Marqueurs d'engagement et de régularité • 
                  Lecture qualitative et quantitative des résultats
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Impacts Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="font-body text-sm tracking-[0.2em] uppercase text-sage mb-4 block">
              Impacts observables
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
              Ce qui change pour vos équipes
            </h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {impacts.map((impact, index) => (
              <motion.div
                key={impact.label}
                variants={staggerItem}
                whileHover={{ y: -5, scale: 1.02 }}
                className="p-6 rounded-2xl bg-[#0F0F0F] border border-white/5 hover:border-sage/20 transition-all duration-500"
              >
                <div className="flex items-center gap-3 mb-3">
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity, delay: index * 0.15 }}
                  >
                    <CheckCircle2 className="text-sage" size={20} />
                  </motion.div>
                  <h3 className="font-heading text-lg text-sage">
                    {impact.label}
                  </h3>
                </div>
                <p className="font-body text-gray-400">
                  {impact.value}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Differentiators Section */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div {...fadeUp}>
              <span className="font-body text-sm tracking-[0.2em] uppercase text-sage mb-4 block">
                Ce qui nous différencie
              </span>
              <h2 className="font-heading text-4xl md:text-5xl text-white mb-8">
                Pas un prestataire bien-être. Un partenaire engagement.
              </h2>
              <p className="font-body text-gray-400 leading-relaxed mb-8">
                La différence entre une action bien-être et un vrai levier de performance ? 
                La structure, la durée, la mesure – et l'engagement mutuel.
              </p>

              <motion.div 
                className="space-y-6"
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
              >
                {differentiators.map((item, index) => (
                  <motion.div
                    key={item.title}
                    variants={staggerItem}
                    whileHover={{ x: 8 }}
                    className="border-l-2 border-sage/50 pl-6 hover:border-sage transition-colors"
                  >
                    <h3 className="font-heading text-xl text-white mb-2">{item.title}</h3>
                    <p className="font-body text-gray-400">{item.description}</p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div {...fadeUp} transition={{ delay: 0.2 }}>
              <motion.div 
                className="relative"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.5 }}
              >
                <img
                  src="https://images.unsplash.com/photo-1764093092159-210693ac584f?auto=format&fit=crop&w=800&q=80"
                  alt="Lever de soleil sur les montagnes"
                  className="rounded-3xl w-full h-[400px] object-cover"
                  onLoad={(e) => e.target.classList.add('loaded')}
                />
              </motion.div>

              <motion.div 
                className="mt-8 p-8 rounded-2xl glass"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
              >
                <div className="flex items-center gap-4 mb-4">
                  <Sparkles className="text-sage" size={24} />
                  <h4 className="font-heading text-xl text-white">Pour les décideurs</h4>
                </div>
                <p className="font-body text-gray-400">
                  Un échange de 30 minutes pour comprendre votre contexte, 
                  vos enjeux, et déterminer si une collaboration fait sens.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div {...fadeUp}>
              <span className="font-body text-sm tracking-[0.2em] uppercase text-sage mb-4 block">
                Prochaine étape
              </span>
              <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
                Discutons de votre entreprise
              </h2>
              <p className="font-body text-gray-400 mb-8 leading-relaxed">
                Un échange stratégique pour comprendre votre contexte, vos défis, 
                et explorer comment nous pouvons accompagner vos équipes vers 
                une performance plus durable.
              </p>
              
              <motion.div 
                className="space-y-4 mb-8"
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
              >
                {[
                  { num: '1', text: 'Vous nous contactez' },
                  { num: '2', text: 'On planifie un échange de 30 minutes' },
                  { num: '3', text: 'On analyse vos besoins ensemble' },
                ].map((step, index) => (
                  <motion.div 
                    key={step.num}
                    variants={staggerItem}
                    className="flex items-center gap-3"
                  >
                    <motion.span 
                      className="w-8 h-8 rounded-full bg-sage/20 flex items-center justify-center text-sage font-body text-sm"
                      whileHover={{ scale: 1.1, backgroundColor: 'rgba(143, 188, 143, 0.3)' }}
                    >
                      {step.num}
                    </motion.span>
                    <p className="font-body text-white">{step.text}</p>
                  </motion.div>
                ))}
              </motion.div>

              <p className="font-body text-sm text-gray-500">
                Réponse sous 24h • Confidentiel
              </p>
            </motion.div>

            <ContactForm
              contactType="b2b"
              title="Votre demande"
              subtitle="Présentez brièvement votre entreprise et vos enjeux."
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default B2BPage;
